import React, { useEffect, useState, useCallback } from "react";
import {
  CheckCircleFilled,
  InfoCircleFilled,
  WarningFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { TOAST_EVENT, TOAST_DISMISS_EVENT } from "../lib/toast";

const ICONS = {
  success: <CheckCircleFilled />,
  info: <InfoCircleFilled />,
  warning: <WarningFilled />,
  error: <CloseCircleFilled />,
  loading: <LoadingOutlined spin />,
};

/**
 * Renders the stack of on-brand gradient toasts at top-center of the screen.
 * Listens to the window APP_TOAST / APP_TOAST_DISMISS events so both React and
 * plain Three.js modules can trigger it via lib/toast's helpers.
 *
 * Rendered inside the themed ConfigProvider (DesignerLayout) — but it doesn't
 * depend on antd's theme tokens; styling comes from index.css (.app-toast*).
 */
const ToastHost = () => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const handle = (event) => {
      const detail = event?.detail;
      if (!detail?.id || !detail?.message) return;

      setToasts((prev) => {
        // Replace an existing toast with the same id/key (e.g. loading -> done),
        // otherwise append a new one.
        const exists = prev.some((t) => t.id === detail.id);
        return exists
          ? prev.map((t) => (t.id === detail.id ? detail : t))
          : [...prev, detail];
      });

      if (detail.duration > 0) {
        window.setTimeout(() => dismiss(detail.id), detail.duration);
      }
    };

    const handleDismiss = (event) => {
      const id = event?.detail?.id;
      if (id) dismiss(id);
    };

    window.addEventListener(TOAST_EVENT, handle);
    window.addEventListener(TOAST_DISMISS_EVENT, handleDismiss);
    return () => {
      window.removeEventListener(TOAST_EVENT, handle);
      window.removeEventListener(TOAST_DISMISS_EVENT, handleDismiss);
    };
  }, [dismiss]);

  if (!toasts.length) return null;

  return (
    <div className="app-toast-host" role="region" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`app-toast app-toast--${t.type}`}
          role="status"
          onClick={() => dismiss(t.id)}
        >
          <span className={`app-toast__icon app-toast__icon--${t.type}`}>
            {ICONS[t.type] || ICONS.info}
          </span>
          <span className="app-toast__body">
            <span className="app-toast__message">{t.message}</span>
            {t.description ? (
              <span className="app-toast__description">{t.description}</span>
            ) : null}
          </span>
          {t.action ? (
            <button
              type="button"
              className="app-toast__action"
              onClick={(e) => {
                // Don't let the click bubble to the toast's dismiss handler
                // before the action runs.
                e.stopPropagation();
                try {
                  t.action.onClick();
                } finally {
                  dismiss(t.id);
                }
              }}
            >
              {t.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default ToastHost;
